<script setup lang="ts">
import { partsDuPanier } from "~/utils/plusieursFois";
import { dernierJourPlusieursFoisAdhesion, type DureeTarifAdhesion } from "@donoor/utils";
import { useDemoAccueil } from "~/composables/useDemoAccueil";
import { remonterAChaqueEtape } from "~/utils/defilementEtape";
import { getDonoorTipAmountAchat } from "@donoor/utils";
import { resolveCardProvider } from "@donoor/checkout";
import { usePaymentFormLifecycle } from "~/composables/payment/usePaymentFormLifecycle";
import { fournirPaysAssociation } from "~/composables/usePaysAssociation";
import { useMembershipPlanChatContext } from "~/composables/useMembershipPlanChatContext";
/**
 * Page publique d'un plan d'adhésion
 * UX inspirée de la page événement : bannière, 2 colonnes, checkout intégré
 * Supporte les commandes multi-membres (famille/groupe)
 */
definePageMeta({ layout: false });

const { t } = useI18n();
const route = useRoute();
// Démo de la home (useDemoAccueil) : la page y est affichée telle quelle, son
// slug vient de la démo (l'adresse est celle de la home).
const demoAccueil = useDemoAccueil();
const slug = computed(
  () =>
    demoAccueil.integree?.slug ??
    ((route.params as Record<string, string | string[] | undefined>).slug as string),
);
const config = useRuntimeConfig();

// Mode widget embarqué (?embed=1) : masque l'habillage (banner/header/sidebar/
// footer), fond transparent, auto-resize vers l'hôte. Le tunnel est inchangé.
const embedRootEl = ref<HTMLElement | null>(null);
const { isEmbed, hostOrigin } = useEmbedFrame(embedRootEl);

// Mode APERÇU du dashboard (?preview=true) : la page est cadrée en iframe dans
// la section « Aperçu » de la gestion d'adhésion. Deux effets — l'API renvoie
// aussi les plans en BROUILLON, et le brouillon en cours d'édition est poussé
// en direct par postMessage. Parité billetterie. Karim 2026-08-30.
const isPreview = computed(() => route.query.preview === "true");

// Fetch SSR + disponibilité (404 archived/draft/not_found) + plans frères +
// helpers par-plan + auto-refresh member_count (composable dédié)
const {
  plan,
  planOptions,
  customFields,
  isInitialLoading,
  isEntityMissing,
  missingCopy,
  allPlans,
  hasMultiplePlans,
  groupTitle,
  isAllPlansClosed,
  isAllSeasonsEnded,
  getPlanOptions,
  getPlanCustomFields,
  prorateInfo,
  hasProrateDiscount,
  getPlanPrice,
  getPlanTitle,
} = await useMembershipPlanData(slug, isPreview);

// Vrai statut HTTP 404 côté SSR quand le plan n'existe pas (404 API explicite
// uniquement — jamais sur erreur transitoire). Google reçoit le bon signal ;
// l'humain voit la page « introuvable » + suggestions. Audit SEO 2026-08-01.
// Jamais dans la démo de la home : c'est la home qui recevrait le 404.
if (import.meta.server && isEntityMissing.value && !demoAccueil.estIntegree) {
  const event = useRequestEvent();
  if (event) setResponseStatus(event, 404);
}

// Pays de l'association : les étapes du tunnel y lisent les téléphones.
fournirPaysAssociation(() => plan.value?.country);

// SEO + JSON-LD (composable dédié pour réduire la taille du fichier)
const { planCoverImageId } = useMembershipCheckoutSeo(plan, slug);

/**
 * Couleur des boutons — MÊME système que la billetterie et la boutique
 * (`--accent` / `--accent-text`). Les adhésions n'en avaient aucun : leurs
 * boutons étaient noirs en dur, quelle que soit la charte de l'association.
 * Karim 2026-09-12.
 */
const { accentStyle } = useEventAccentColor(plan);

// Plan status (composable dédié)
const { isPlanActive, isPlanFull, remainingPlaces, isSeasonEnded, durationLabel } =
  useMembershipPlanStatus(plan, isPreview);

// MULTI-MEMBER: Quantity & Participants (composable dédié)
const {
  planQuantities,
  payerIndex,
  quantity,
  maxQuantity,
  maxQuantityPerPlan,
  participants,
  firstParticipant,
  incrementPlanQuantity,
  decrementPlanQuantity,
  incrementQuantity,
  decrementQuantity,
  buildParticipantsList,
} = useMembershipParticipants({
  plan,
  allPlans,
  hasMultiplePlans,
  remainingPlaces,
  getPlanOptions,
  getPlanTitle,
  getPlanPrice,
});
type ParticipantLocal = (typeof participants.value)[number];

// File upload composable
const {
  uploadCustomFieldFile,
  removeUploadedFile,
  isFileUploaded,
  isFileUploading,
  getFileUploadError,
  isAnyFileUploading,
} = useMembershipFileUpload(participants, config.public.apiBaseUrl as string);

// Buyer info (person who pays, step 3)
const buyerInfo = ref({
  firstName: "",
  lastName: "",
  email: "",
  confirmEmail: "",
  phone: "",
});

// ==========================================
// Payment calculations
// ==========================================

const {
  stripeAccountId,
  paymentProviders,
  stripeChargesEnabled,
  paypalChargesEnabled,
  selectedProvider,
  isPaypalOnlyMode,
  preferredProvider,
  paypalCardFieldsEnabled,
} = usePaymentProvidersState(plan);

// Provider du FORMULAIRE CARTE (Karim 2026-08-30) : `preferred_provider` = où
// l'asso veut recevoir l'argent (bouton PayPal, wallets), mais il n'impose les
// CardFields PayPal que si PayPal sait les rendre (capability PPCP_CUSTOM).
// Sinon, Stripe actif → carte Stripe. Jamais de cadre vide. Règle unique dans
// `resolveCardProvider` (@donoor/checkout) — ne PAS la réimplémenter ici.
// + Bouton PayPal alternative si PayPal aussi configuré.
const usesPaypalCard = computed(
  () =>
    resolveCardProvider({
      stripe: stripeChargesEnabled.value,
      paypal: paypalChargesEnabled.value,
      paypalCardFields: paypalCardFieldsEnabled.value,
      preferred: preferredProvider.value,
    }) === "paypal",
);
const usesPaypalWallets = computed(
  () => isPaypalOnlyMode.value || usesPaypalCard.value,
);
const showPayPalAlternative = computed(
  () => stripeChargesEnabled.value && paypalChargesEnabled.value,
);

const isPaymentConfigured = computed(() =>
  paymentProviders.value.accepts_payments || plan.value?.pricing === "free",
);
const donoorFeeRate = computed(() => (plan.value?.donoor_fee_rate ?? 0) / 100);
const hasFees = computed(() => donoorFeeRate.value > 0);

const includeFees = ref(true);
const donateToUsAmount = ref<number>(0);
const donateUs = ref(true);
// Don à l'association (migration 291) — réglage « Don libre » du tarif.
const showDonation = computed(() => plan.value?.allow_donation !== false);
const showDonationInput = ref(false);
const donationAmount = ref(0);

const originalPlanPrice = computed(() => Number(plan.value?.price || 0));
const isFlexiblePricing = computed(() => plan.value?.pricing === "flexible");
const isFreePricing = computed(() => plan.value?.pricing === "free");
const flexiblePrice = ref<number | null>(null);
watch(
  () => plan.value,
  (p) => {
    if (p && p.pricing === "flexible" && flexiblePrice.value === null) {
      flexiblePrice.value = Number(p.min_price ?? 0) || 0;
    }
  },
  { immediate: true },
);

const planPrice = computed(() => {
  if (isFreePricing.value) return 0;
  if (isFlexiblePricing.value) return Number(flexiblePrice.value || 0);
  if (prorateInfo.value) return prorateInfo.value.proratedPrice;
  return originalPlanPrice.value;
});

const totalPlanPrice = computed(() => {
  if (hasMultiplePlans.value) {
    let total = 0;
    for (const ap of allPlans.value) {
      const qty = planQuantities.value[ap.id] || 0;
      total += Number(ap.price || 0) * qty;
    }
    return total;
  }
  return planPrice.value * quantity.value;
});

const totalOptionsPrice = computed(() => {
  let total = 0;
  for (const p of participants.value) {
    const pOptions = getPlanOptions(p.planId);
    for (const opt of pOptions) {
      if (!opt.is_active) continue;
      if (p.selectedOptions[opt.id]) total += Number(opt.price || 0);
    }
  }
  return total;
});

const subtotal = computed(() => totalPlanPrice.value + totalOptionsPrice.value);

// PROMO CODE (composable dédié)
const { promoCode, promoLoading, promoError, promoApplied, promoDiscount, applyPromoCode, removePromo } =
  useMembershipPromoCode(slug, subtotal);

const totalPrice = computed(() => Math.max(0, subtotal.value - promoDiscount.value));
const isFreeOrder = computed(() => totalPrice.value === 0);

const requiresApproval = computed(() => !!plan.value?.require_approval);

function getParticipantPrice(p: ParticipantLocal): number {
  let total = p.planPrice;
  const pOptions = getPlanOptions(p.planId);
  for (const opt of pOptions) {
    if (!opt.is_active) continue;
    if (p.selectedOptions[opt.id]) total += Number(opt.price || 0);
  }
  return total;
}

// Le soutien Donoor est suggéré à partir du montant AVANT remise (`subtotal`),
// JAMAIS du total remisé : un code promo ne doit pas réduire ce qui revient à
// Donoor. Adhésion 10 € + 2 € de soutien avec un code 100 % → 0 € pour l'asso,
// 2 € pour Donoor. Et dès que l'adhérent a touché au curseur, on ne réécrit
// plus sa valeur. Karim 2026-08-07.
const donateToUsTouched = ref(false);
watch(subtotal, (base) => {
  if (donateToUsTouched.value) return;
  // Achat : suggestion plafonnée à 10 € (cf. @donoor/utils tip.ts).
  donateToUsAmount.value = getDonoorTipAmountAchat(base);
}, { immediate: true });

function setDonateToUsAmount(value: number) {
  donateToUsTouched.value = true;
  donateToUsAmount.value = value;
}

/**
 * Un paiement est nécessaire dès qu'il reste quelque chose à encaisser — y
 * compris quand SEUL le soutien Donoor subsiste (commande remisée à 100 % ou
 * adhésion gratuite). Parité /b/[slug]. Karim 2026-08-07.
 */
const needsPayment = computed(
  () =>
    totalPrice.value > 0 ||
    donationAmount.value > 0 ||
    (donateUs.value && donateToUsAmount.value > 0),
);

const { estimateProvider, paypalFeePercent, paypalFeeFixed } =
  usePaypalFeeEstimate(paymentProviders, ref(false));
const calculations = usePaymentCalculations({
  amount: computed(() => String(totalPrice.value)),
  includeFees,
  donoorFeeRate,
  isSubscription: ref(false),
  subscriptionPeriod: ref("monthly"),
  donateToUsAmount,
  paymentProvider: estimateProvider,
  paypalFeePercent,
  paypalFeeFixed,
  // Le SERVEUR fixe le montant débité : on reproduit son arrondi à
  // l'identique, sinon affiché ≠ débité d'un centime (audit 2026-09-23).
  arrondiServeur: true,
  donAssociation: donationAmount,
});

// === useCheckout (@donoor/checkout) ===
const stripeInitError = ref(false);
const stripeErrorMessage = ref("");

const orderError = ref<string | null>(null);
const orderErrorTechnical = ref(false);

// Payload de commande (factory pure — déclarée AVANT les providers qui la
// consomment dans leurs callbacks createPaymentIntent).
// Paiement en plusieurs fois (0 = en une fois) — Karim 2026-10-21.
const nombreParts = ref(0);

const { buildOrderPayload } = useMembershipOrderPayload({
  nombreParts,
  plan,
  participants,
  buyerInfo,
  totalPrice,
  includeFees,
  donateUs,
  donateToUsAmount,
  donationAmount,
  isFlexiblePricing,
  flexiblePrice,
  promoApplied,
  selectedProvider,
  getPlanOptions,
});

// Instances de paiement Stripe + PayPal + wallets (composable dédié)
const { checkout, paypalCheckout, mountPrimaryWallets, setPendingStripeOrder } =
  useMembershipCheckoutProviders({
    slug,
    plan,
    stripeAccountId,
    paymentProviders,
    isPaypalOnlyMode,
    usesPaypalWallets,
    selectedProvider,
    includeFees,
    calculations,
    buyerInfo,
    buildOrderPayload,
  });

// ==========================================
// Checkout steps
// ==========================================
const checkoutStep = ref(1);
const isProcessing = ref(false);

// Modal de progression paiement (création → paiement → confirmation → success).
// Parité totale avec /b/[slug] event (Karim 2026-06-21). Le composable
// `useMembershipOrderSubmit` met à jour `processingStep`/`processingProgress`
// au fil des étapes et `<AslugProcessingOverlay>` s'affiche dès `isProcessing`.
const { processingStep, processingProgress, startProgress, stopProgress } =
  useCheckoutProcessingProgress();
function dismissProcessingError() {
  isProcessing.value = false;
  processingStep.value = "creating";
  processingProgress.value = 0;
  stopProgress();
}

const {
  sessionExpiredModal,
  dismissSessionExpired,
  saveCheckoutState,
  restoreCheckoutState,
  clearCheckoutSession,
} = useMembershipCheckoutSession(
  slug,
  checkoutStep,
  {
    planQuantities,
    // Cast — composable signature uses unknown[] / unknown to avoid coupling to local types
    participants: participants as unknown as Ref<unknown[]>,
    buyerInfo: buyerInfo as unknown as Ref<unknown>,
    includeFees,
    donateUs,
    promoApplied: promoApplied as unknown as Ref<unknown>,
  },
  () => ({
    planQuantities: planQuantities.value,
    participants: participants.value,
    buyerInfo: buyerInfo.value,
    includeFees: includeFees.value,
    donateUs: donateUs.value,
    promoApplied: promoApplied.value,
  }),
);

// Étape « Participants » TOUJOURS présente, même pour une seule adhésion
// (Karim 2026-09-21) : c'est là que se saisissent le nom, le prénom, l'email
// et le téléphone de l'ADHÉRENT. Avant, à quantité 1, l'étape disparaissait et
// l'identité de l'adhérent était déduite du payeur — les deux ne sont pas
// forcément la même personne, et les champs personnalisés se retrouvaient
// mélangés à l'étape 1.
const hasParticipantsStep = computed(() => true);
const stepLabels = computed(() => [
  t("adhesion.aSlugIndex.etapeAdhesion"),
  t("adhesion.aSlugIndex.etapeParticipants"),
  t("adhesion.aSlugIndex.etapeCoordonnees"),
  t("adhesion.aSlugIndex.etapeRecapitulatif"),
]);

const stepParticipants = computed(() => 2);
const stepCoordonnees = computed(() => 3);
const stepRecap = computed(() => 4);

const { canGoFromStep1, allParticipantsCustomFieldsValid, coordsValid } =
  useMembershipCheckoutValidation({
    plan,
    quantity,
    isFlexiblePricing,
    flexiblePrice,
    isAnyFileUploading,
    hasMultiplePlans,
    participants,
    customFields,
    getPlanCustomFields,
    buyerInfo,
  });

function goToParticipants() {
  if (!canGoFromStep1.value) return;
  buildParticipantsList();
  if (hasParticipantsStep.value) {
    checkoutStep.value = stepParticipants.value;
  } else {
    const p = participants.value[0];
    if (p) {
      buyerInfo.value.firstName = p.firstName || buyerInfo.value.firstName;
      buyerInfo.value.lastName = p.lastName || buyerInfo.value.lastName;
      buyerInfo.value.email = p.email || buyerInfo.value.email;
      buyerInfo.value.confirmEmail = p.email || buyerInfo.value.confirmEmail;
      buyerInfo.value.phone = p.phone || buyerInfo.value.phone;
    }
    checkoutStep.value = stepCoordonnees.value;
  }
}

function goToCoordonnees() {
  if (!allParticipantsCustomFieldsValid.value) return;
  const payer = participants.value[payerIndex.value];
  if (payer) {
    buyerInfo.value.firstName = payer.firstName;
    buyerInfo.value.lastName = payer.lastName;
    buyerInfo.value.email = payer.email;
    buyerInfo.value.confirmEmail = payer.email;
    buyerInfo.value.phone = payer.phone;
  }
  checkoutStep.value = stepCoordonnees.value;
}

function setAsPayer(idx: number) { payerIndex.value = idx; }

/**
 * Formulaire de paiement (carte + wallets) : préparé tant que le bloc paiement
 * est À L'ÉCRAN (récap + quelque chose à payer + pas d'approbation), rangé
 * sinon — cf. `usePaymentFormLifecycle`.
 *
 * ⚠️ Avant le 2026-09-25 : préparé seulement en ARRIVANT sur le récap, et
 * jamais rangé en le quittant. Adhésion gratuite (ou remisée à 100 %) +
 * pourboire → carte « Chargement… » à l'infini ; récap → Retour → Continuer
 * → champ carte accroché à l'ancien écran.
 */
const paiement = usePaymentFormLifecycle({
  visible: computed(
    () =>
      checkoutStep.value === stepRecap.value &&
      needsPayment.value &&
      !requiresApproval.value &&
      !isPreview.value,
  ),
  monter: async () => {
    // Carte selon le prestataire PRIMAIRE (Karim 2026-06-21) :
    //   usesPaypalCard → PayPal CardFields ; sinon → Stripe Elements.
    const instance = usesPaypalCard.value ? paypalCheckout : checkout;
    const carteOk = usesPaypalCard.value
      ? await paypalCheckout.mountCardForm("paypal-cardfields-container")
      : await checkout.mountCardForm("card-element", { type: "card-legacy" });
    stripeInitError.value = !carteOk;
    stripeErrorMessage.value = carteOk
      ? ""
      : instance.cardFormError.value?.message || t("adhesion.useMembershipOrderSubmit.systemePaiementPasPret");
    // Wallets — route Stripe Express OU PayPal Apple+Google.
    await mountPrimaryWallets();
    return carteOk;
  },
  demonter: () => {
    checkout.destroy();
    paypalCheckout.destroy();
    stripeInitError.value = false;
    stripeErrorMessage.value = "";
  },
  cle: () => usesPaypalCard.value,
});

async function goToRecap() {
  if (!coordsValid.value) return;
  if (quantity.value === 1 && participants.value.length === 1) {
    const p = participants.value[0]!;
    p.firstName = buyerInfo.value.firstName;
    p.lastName = buyerInfo.value.lastName;
    p.email = buyerInfo.value.email;
    p.phone = buyerInfo.value.phone;
  }
  checkoutStep.value = stepRecap.value;
}

function retryStripeInit() {
  void paiement.reessayer();
}

watch([checkoutStep, planQuantities, participants, buyerInfo, includeFees], () => {
  saveCheckoutState();
}, { deep: true });
const wizardTopRef = remonterAChaqueEtape(checkoutStep);

/**
 * Après restauration de session : si on atterrit DIRECTEMENT sur l'étape récap
 * (refresh sur `?step=3`), il faut monter le formulaire de paiement — sinon il
 * n'est jamais initialisé (mountCardForm n'était appelé qu'à la transition
 * step 2→3 via goToRecap) → « Chargement du système de paiement… » infini.
 */
function restoreAndMountIfOnRecap() {
  // Le paiement se prépare tout seul si l'on atterrit sur le récap (état
  // restauré) : cf. `usePaymentFormLifecycle`.
  restoreCheckoutState();
}

onMounted(() => {
  if (plan.value) {
    restoreAndMountIfOnRecap();
  } else {
    const stop = watch(plan, () => {
      if (plan.value) {
        restoreAndMountIfOnRecap();
        stop();
      }
    });
  }
});

// Contexte donné à l'IA du chat : elle aide en priorité sur CETTE adhésion
// tant que le visiteur ne change pas de sujet (Karim 2026-10-12).
useMembershipPlanChatContext(plan);

const { submitOrder, setOrderError, clearOrderError } = useMembershipOrderSubmit({
  slug,
  isProcessing,
  totalPrice,
  isFreeOrder: computed(() => !needsPayment.value),
  requiresApproval,
  stripeInitError,
  stripeErrorMessage,
  orderError,
  orderErrorTechnical,
  promoApplied,
  buyerInfo,
  checkout,
  paypalCheckout,
  usesPaypalCard,
  setPendingOrderResult: setPendingStripeOrder,
  clearCheckoutSession,
  buildOrderPayload,
  // Modal de progression (parité /b event, Karim 2026-06-21)
  processingStep,
  processingProgress,
  startProgress,
  stopProgress,
});

// setOrderError used by goToRecap error handler too
void setOrderError;

// Paiement en plusieurs fois : le dernier paiement tombe avant la fin de
// l'adhésion la plus courte du panier (même règle que le serveur).
const dernierJourPlusieursFois = computed(() => {
  const choisis = allPlans.value.filter((ap) => (planQuantities.value[ap.id] || 0) > 0);
  const tarifs = choisis.length ? choisis : plan.value ? [plan.value] : [];
  return dernierJourPlusieursFoisAdhesion(tarifs as unknown as DureeTarifAdhesion[]);
});

// Paiement en plusieurs fois choisi : les parts (calcul du serveur), pour le
// récapitulatif de droite — total réel, à payer aujourd'hui, puis la suite.
const partsPlusieursFois = computed(() =>
  partsDuPanier({
    nombre: nombreParts.value,
    montantDiviseEuros: totalPrice.value,
    enPlusEuros: donationAmount.value,
    soutienEuros: donateUs.value ? donateToUsAmount.value : 0,
    tauxCommission: donoorFeeRate.value ?? 0,
    fraisCouverts: includeFees.value,
  }),
);
</script>

<template>
  <div
    ref="embedRootEl"
    :class="isEmbed ? 'bg-transparent @container p-4 sm:p-5' : 'min-h-screen flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]'"
    :style="accentStyle"
  >
    <!-- Surveillance : asso suspended/banned → page indisponibilité -->
    <template
      v-if="plan && (plan.organization_risk_status === 'suspended' || plan.organization_risk_status === 'banned')"
    >
      <AppHeader v-if="!isEmbed && !demoAccueil.estIntegree" rayon="adhesions" />
      <OrgUnavailable
        :risk-status="plan.organization_risk_status"
        :reason="plan.organization_risk_reason"
        :organization-name="plan.organization_name"
      />
      <!-- Suggestions + footer : la page ne doit pas être un cul-de-sac,
           même raison que la branche 404 (Karim 2026-08-01). -->
      <div v-if="!isEmbed" class="grow max-w-6xl mx-auto px-4 pb-16">
        <NotFoundSuggestions
            alternative type="adhesions" />
      </div>
      <FooterLite v-if="!isEmbed && !demoAccueil.estIntegree" />
    </template>

    <!-- Loading skeleton — affiché pendant le fetch initial OU pendant
         un retry sur erreur transitoire (timeout/5xx/network). -->
    <PublicPageLoadingSkeleton v-else-if="isInitialLoading || (!plan && !isEntityMissing)" rayon="adhesions" />

    <!-- Error state — UNIQUEMENT sur 404 explicite (entité supprimée/inexistante). -->
    <PublicPageErrorState
      v-else-if="isEntityMissing"
      rayon="adhesions"
      :icon="missingCopy.icon"
      :title="missingCopy.title"
      :message="missingCopy.message"
    >
      <!-- Adhésion supprimée/inexistante → proposer des adhésions ouvertes
           (Karim 2026-08-01 : la page 404 ne doit pas être un cul-de-sac). -->
      <template #suggestions>
        <NotFoundSuggestions type="adhesions" />
      </template>
    </PublicPageErrorState>

    <template v-else-if="plan">
      <!-- Header en overlay (position fixed) au-dessus de la banner -->
      <AppHeader v-if="!isEmbed && !demoAccueil.estIntegree" :links="navLinks" rayon="adhesions" />

        <!-- Mode aperçu : dire que la page n'est pas encore en ligne, sinon
             l'organisation croit consulter sa vraie page publique. Parité
             billetterie. Karim 2026-08-30. -->
        <div
          v-if="isPreview"
          class="bg-amber-500 text-white text-center text-sm font-medium py-2 px-4"
        >
          {{ $t('adhesion.aSlugIndex.modeApercuBandeau') }}
        </div>

        <!-- Titre du GROUPE (sans suffixe « — {tarif} ») : la bannière représente
             la page entière, pas un tarif. Karim 2026-08-04. -->
        <AslugBanner v-if="!isEmbed" :cover-image-id="planCoverImageId" :title="groupTitle" />

        <!-- ⚠️ REMONTE PAR-DESSUS LA BANNIÈRE FIXE (Karim 2026-08-23, même
             traitement que la boutique et la billetterie). `z-10` passe
             au-dessus de la bannière (`z-0`).
             `-mt-[28px]` ici et NON `-mt-[68px]` comme boutique/billetterie :
             celles-ci ont un logo dans le flux qui creuse 40px d'écart en plus
             à rattraper. AslugBanner n'a pas de logo, donc seul le rayon
             (28px) est à couvrir — mesuré, 68px chevauchait 68px au lieu de 28.
             DEUX niveaux : le FOND arrondi couvre toute la largeur, le CONTENU
             reste borné à 1280px. Un seul div ne peut pas faire les deux. -->
        <div
          :class="isEmbed
            ? 'w-full'
            : 'relative z-10 grow -mt-[28px] rounded-t-[28px] bg-[#f7f8fa] dark:bg-[#0a0a0a] pt-8'"
        >
        <div :class="isEmbed ? 'w-full' : 'w-full max-w-[1280px] mx-auto px-3 md:px-6'">
          <div class="flex flex-col lg:flex-row gap-4 md:gap-6 mt-0">
            <div
              class="flex-1 min-w-0 space-y-4 md:space-y-6"
              :class="isEmbed ? 'lg:w-full mt-0' : 'lg:w-3/4 mt-4 md:mt-6'"
            >
              <AslugHeaderCard
                v-if="!isEmbed"
                :plan="plan"
                :display-title="groupTitle"
                :duration-label="durationLabel"
                :is-free-pricing="isFreePricing"
                :is-flexible-pricing="isFlexiblePricing"
                :plan-price="planPrice"
                :original-plan-price="originalPlanPrice"
                :has-prorate-discount="hasProrateDiscount"
                :prorate-info="prorateInfo"
              />

              <PublicPageYoutubeEmbed v-if="!isEmbed && plan?.youtube_url" :url="plan.youtube_url" />

              <!-- Bannière fiscale gatée UNIQUEMENT sur le toggle du plan
                   (is_tax_deductible). Avoir un numéro RNA ne rend PAS un plan
                   déductible — seul le toggle décide (parité page événement
                   /b/[slug] + chantier fiscal : plan non déductible → AUCUNE
                   mention). Karim 2026-06-21. -->
              <PublicPageTaxDeductibleBanner v-if="!isEmbed && plan?.is_tax_deductible" />

              <!-- Plan status -->
              <!-- Clôturé : TOUS les tarifs du groupe sont fermés (aucun
                   achetable). Carte dédiée À LA PLACE du wizard. Si au moins un
                   tarif reste actif, le wizard s'affiche et les tarifs `closed`
                   sont grisés par-tarif (AslugStepPlans). Karim 2026-06-27. -->
              <AslugStatusCard
                v-if="isAllPlansClosed"
                icon="i-lucide-lock"
                :title="$t('adhesion.aSlugIndex.clotureTitre')"
                :message="$t('adhesion.aSlugIndex.clotureMessage')"
              />
              <AslugStatusCard
                v-else-if="!isPlanActive"
                icon="i-lucide-info"
                :title="$t('adhesion.aSlugIndex.indisponibleTitre')"
                :message="$t('adhesion.aSlugIndex.indisponibleMessage')"
              />
              <AslugStatusCard
                v-else-if="isPlanFull"
                icon="i-lucide-users"
                :title="$t('adhesion.aSlugIndex.completTitre')"
                :message="$t('adhesion.aSlugIndex.completMessage')"
              />
              <!-- Saison terminée pour TOUS les tarifs (ou pour le seul plan
                   chargé). Un tarif encore ouvert → wizard, les tarifs
                   terminés y sont grisés (AslugStepPlans). Karim 2026-10-09. -->
              <AslugStatusCard
                v-else-if="isAllSeasonsEnded || (allPlans.length === 0 && isSeasonEnded)"
                icon="i-lucide-calendar-x"
                :title="$t('adhesion.aSlugIndex.saisonTermineeTitre')"
                :message="$t('adhesion.aSlugIndex.saisonTermineeMessage')"
              />
              <!-- ⚠️ PAS en aperçu (Karim 2026-09-10) : ce bloc remplaçait le
                   tunnel, or c'est lui que l'organisation vient vérifier. Le
                   paiement reste neutralisé en aperçu. -->
              <PublicPagePaymentUnavailable
                v-else-if="!isPaymentConfigured && !isPreview"
                :message="$t('adhesion.aSlugIndex.paiementNonConfigureMessage')"
              />

              <!-- Checkout Flow -->
              <div v-else ref="wizardTopRef" class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden scroll-mt-4">
                <!-- Step tabs -->
                <div class="flex items-center border-b border-gray-100 dark:border-gray-800">
                  <button
                    v-for="(label, idx) in stepLabels"
                    :key="idx"
                    class="flex-1 flex items-center justify-center gap-1.5 px-1 py-3 md:py-4 text-xs md:text-sm font-semibold transition-all relative"
                    :class="checkoutStep === idx + 1
                      ? 'text-[#212121] dark:text-white'
                      : checkoutStep > idx + 1
                        ? 'text-[#212121] dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                        : 'text-gray-300 dark:text-gray-600'"
                    :disabled="idx + 1 > checkoutStep"
                    @click="idx + 1 < checkoutStep && (checkoutStep = idx + 1)"
                  >
                    <div
                      class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors"
                      :class="checkoutStep === idx + 1
                        ? 'accent-btn'
                        : checkoutStep > idx + 1
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'"
                    >
                      <UIcon v-if="checkoutStep > idx + 1" name="i-lucide-check" class="size-3.5" />
                      <span v-else>{{ idx + 1 }}</span>
                    </div>
                    <span class="hidden sm:inline">{{ label }}</span>
                    <div v-if="checkoutStep === idx + 1" class="absolute bottom-0 left-4 right-4 h-0.5 bg-[#212121] dark:bg-white rounded-full" />
                  </button>
                </div>

                <Transition
                  mode="out-in"
                  enter-active-class="transition-all duration-300 ease-out"
                  enter-from-class="opacity-0 translate-y-2"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-active-class="transition-all duration-150 ease-in"
                  leave-from-class="opacity-100 translate-y-0"
                  leave-to-class="opacity-0 -translate-y-2"
                >
                  <AslugStepPlans
                    v-if="checkoutStep === 1"
                    key="step1"
                    :plan="plan"
                    :all-plans="allPlans"
                    :has-multiple-plans="hasMultiplePlans"
                    :plan-quantities="planQuantities"
                    :quantity="quantity"
                    :max-quantity="maxQuantity"
                    :max-quantity-per-plan="maxQuantityPerPlan"
                    :remaining-places="remainingPlaces"
                    :duration-label="durationLabel"
                    :is-flexible-pricing="isFlexiblePricing"
                    :is-free-pricing="isFreePricing"
                    :flexible-price="flexiblePrice"
                    :plan-price="planPrice"
                    :plan-options="planOptions"
                    :custom-fields="customFields"
                    :first-participant="firstParticipant"
                    :is-free-order="isFreeOrder"
                    :total-price="totalPrice"
                    :can-go-from-step1="canGoFromStep1"
                    :is-file-uploading="isFileUploading"
                    :is-file-uploaded="isFileUploaded"
                    :get-file-upload-error="getFileUploadError"
                    @increment-plan="incrementPlanQuantity"
                    @decrement-plan="decrementPlanQuantity"
                    @increment="incrementQuantity"
                    @decrement="decrementQuantity"
                    @update:flexible-price="flexiblePrice = $event"
                    @build-participants="buildParticipantsList"
                    @continue="goToParticipants"
                    @upload-file="uploadCustomFieldFile"
                    @remove-file="removeUploadedFile"
                  />

                  <AslugStepParticipants
                    v-else-if="hasParticipantsStep && checkoutStep === stepParticipants"
                    key="step-participants"
                    :participants="participants"
                    :payer-index="payerIndex"
                    :get-plan-options="getPlanOptions"
                    :get-plan-custom-fields="getPlanCustomFields"
                    :all-participants-custom-fields-valid="allParticipantsCustomFieldsValid"
                    :is-file-uploading="isFileUploading"
                    :is-file-uploaded="isFileUploaded"
                    :get-file-upload-error="getFileUploadError"
                    @set-payer="setAsPayer"
                    @back="checkoutStep = 1"
                    @continue="goToCoordonnees"
                    @upload-file="uploadCustomFieldFile"
                    @remove-file="removeUploadedFile"
                  />

                  <AslugStepCoordonnees
                    v-else-if="checkoutStep === stepCoordonnees"
                    key="step-coordonnees"
                    v-model="buyerInfo"
                    :has-participants-step="hasParticipantsStep"
                    @back="checkoutStep = hasParticipantsStep ? stepParticipants : 1"
                    @continue="goToRecap"
                  />

                  <AslugStepRecap
                    v-else-if="checkoutStep === stepRecap"
                    key="step-recap"
                    :is-preview="isPreview"
                    :plan="plan"
                    :participants="participants"
                    :has-multiple-plans="hasMultiplePlans"
                    :all-plans="allPlans"
                    :plan-quantities="planQuantities"
                    :quantity="quantity"
                    :total-plan-price="totalPlanPrice"
                    :total-options-price="totalOptionsPrice"
                    :subtotal="subtotal"
                    :total-price="totalPrice"
                    :promo-code="promoCode"
                    :promo-loading="promoLoading"
                    :promo-error="promoError"
                    :promo-applied="promoApplied"
                    :promo-discount="promoDiscount"
                    :needs-payment="needsPayment"
                    :requires-approval="requiresApproval"
                    :show-donation="showDonation"
                    :show-donation-input="showDonationInput"
                    :donation-amount="donationAmount"
                    :include-fees="includeFees"
                    :donate-us="donateUs"
                    :donate-to-us-amount="donateToUsAmount"
                    :has-fees="hasFees"
                    :calculations="calculations"
                    :stripe-checkout="checkout"
                    :paypal-checkout="paypalCheckout"
                    :payment-form="{ isSubscription: ref(false) }"
                    :uses-paypal-card="usesPaypalCard"
                    :uses-paypal-wallets="usesPaypalWallets"
                    :is-paypal-only-mode="isPaypalOnlyMode"
                    :show-pay-pal-alternative="showPayPalAlternative"
                    :stripe-charges-enabled="stripeChargesEnabled"
                    :paypal-charges-enabled="paypalChargesEnabled"
                    :stripe-init-error="stripeInitError"
                    :stripe-error-message="stripeErrorMessage"
                    :checkout-is-initialized="checkout.cardMounted.value"
                    :checkout-error="checkout.error.value"
                    :order-error="orderError"
                    :order-error-technical="orderErrorTechnical"
                    :is-processing="isProcessing"
                    :get-participant-price="getParticipantPrice"
                    :reglage-plusieurs-fois="plan.installments_counts"
                    :dernier-jour-plusieurs-fois="dernierJourPlusieursFois"
                    :nombre-parts="nombreParts"
                    :taux-commission="donoorFeeRate"
                    @update:nombre-parts="nombreParts = $event"
                    @update:promo-code="promoCode = $event"
                    @apply-promo="applyPromoCode"
                    @remove-promo="removePromo"
                    @update:include-fees="includeFees = $event"
                    @update:show-donation-input="showDonationInput = $event"
                    @update:donation-amount="donationAmount = $event"
                    @update:donate-to-us-amount="setDonateToUsAmount($event)"
                    @retry-stripe-init="retryStripeInit"
                    @clear-order-error="clearOrderError"
                    @back="checkoutStep = stepCoordonnees"
                    @submit="submitOrder"
                  />
                </Transition>
              </div>

              <!-- `mb-10` : le composant ne porte qu'un `py-3` interne, sans
                   cette marge le bloc « Paiement sécurisé » touche le pied de
                   page (Karim 2026-08-23). Même valeur que la boutique. -->
              <PublicPageSecurityBadges v-if="!isEmbed" class="mb-10" />
              <!-- Embed : « Propulsé par Donoor.org » en bas, centré — parité
                   boutique (Karim 31/08). -->
              <div v-if="isEmbed" class="mt-8 mb-6 flex justify-center">
                <EmbedPoweredBy type="adhesion" :host-origin="hostOrigin" />
              </div>
            </div>

            <AslugSidebar
              v-if="!isEmbed"
              :plan="plan"
              :has-multiple-plans="hasMultiplePlans"
              :all-plans="allPlans"
              :plan-quantities="planQuantities"
              :quantity="quantity"
              :duration-label="durationLabel"
              :total-price="totalPrice"
              :checkout-step="checkoutStep"
              :step-recap="stepRecap"
              :total-plan-price="totalPlanPrice"
              :total-options-price="totalOptionsPrice"
              :subtotal="subtotal"
              :promo-discount="promoDiscount"
              :promo-applied="promoApplied"
              :promo-code="promoCode"
              :promo-loading="promoLoading"
              :promo-error="promoError"
              :fees-amount="calculations.feesAmount.value"
              :include-fees="includeFees"
              :donate-us="donateUs"
              :donate-to-us-amount="donateToUsAmount"
              :donation-amount="requiresApproval ? 0 : donationAmount"
              :parts-plusieurs-fois="partsPlusieursFois"
              @update:promo-code="promoCode = $event"
              @apply-promo="applyPromoCode"
              @remove-promo="removePromo"
            />
          </div>
        </div>
        </div>

      <!-- Section « Découvrez d'autres adhésions » MASQUÉE (Karim 2026-08-06,
           pattern HelloAsso) : les assos ne veulent pas les offres des autres
           sur leur page. Gardée en commentaire pour réactivation facile.
      <MembershipRelatedSection v-if="!isEmbed" :slug="slug" />
      -->

      <!-- Footer LÉGER (pattern HelloAsso, unifié avec /c). Karim 2026-08-06.
           L'espacement au-dessus vient désormais de la marge BASSE du bandeau
           « Paiement sécurisé » (cf. MembershipPaymentSecurityBadges), et non
           d'une marge haute sur le footer : celle-ci s'appliquait à toutes les
           pages du site alors qu'elle ne servait qu'ici. -->
      <FooterLite v-if="!isEmbed && !demoAccueil.estIntegree" />
    </template>

    <PublicPageSessionExpiredModal
      variant="a"
      :open="sessionExpiredModal"
      :message="$t('adhesion.aSlugIndex.sessionExpiree')"
      @dismiss="dismissSessionExpired"
    />

    <!-- Modal de progression paiement (création → paiement → confirmation →
         success). Parité totale avec /b/[slug] event. Karim 2026-06-21. -->
    <AslugProcessingOverlay
      :open="isProcessing"
      :step="processingStep"
      :progress="processingProgress"
      :order-error="orderError"
      :order-error-technical="orderErrorTechnical"
      :contact-email="plan?.contact_email"
      @dismiss-error="dismissProcessingError"
    />
  </div>
</template>

<style scoped>
/* Boutons d'action — couleur choisie dans la configuration de l'adhésion.
   Repli #212121 quand aucune couleur n'est posée. Même classe et mêmes
   variables que la billetterie (BslugStepRecap) : une seule règle pour les
   trois domaines. Karim 2026-09-12. */
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
